import { useEffect } from 'react';
import { toast } from 'react-toastify';

import { useMutation, useQuery } from '@tanstack/react-query';
import { apiServiceProxy } from 'v2/services/api-proxy';

const EMBEDDED_BROWSER_PATTERN = /WhatsApp|Instagram|FBAN|FBAV|FB_IAB|Line\/|MicroMessenger|TikTok|Snapchat/i;
const FORCED_DOWNLOAD_TYPE = 'application/octet-stream';

export type DownloadFile = {
  id: number;
  name: string;
  extension: string;
  url_download: string;
};

const buildFileName = (file: DownloadFile) => {
  const extension = file.extension.replace(/^\./, '').trim();
  const name = file.name.trim() || 'arquivo';

  if (!extension) {
    return name;
  }

  if (name.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
    return name;
  }

  return `${name}.${extension}`;
};

const triggerAnchorDownload = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
};

const saveBlob = async (blob: Blob, filename: string) => {
  const file = new File([blob], filename, { type: FORCED_DOWNLOAD_TYPE });
  const isEmbeddedBrowser = EMBEDDED_BROWSER_PATTERN.test(navigator.userAgent);

  if (isEmbeddedBrowser && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
    }
  }

  triggerAnchorDownload(file, filename);
};

export const useDownload = (hash: string) => {
  const listDownloads = useQuery({
    queryKey: ['downloadFile', hash],
    queryFn: async () => {
      const result = await apiServiceProxy.get<DownloadFile[]>(`/downloadfile/${hash}`);
      return result.data;
    },
    enabled: !!hash
  });

  useEffect(() => {
    if (listDownloads.isError) {
      toast.error('Ops, erro na listagem de downloads');
    }
  }, [listDownloads.isError]);

  const validateDownloadUrl = async (url: string): Promise<boolean> => {
    const fileHash = url.split('/').pop();
    const result = await apiServiceProxy.post('/validatedownloadhash', { hash: fileHash });
    return result.data?.status;
  };

  const getDownloadUrl = async (file: DownloadFile): Promise<string | null> => {
    const isValid = await validateDownloadUrl(file.url_download);

    if (isValid) {
      return file.url_download;
    }

    const refreshedDownloads = await listDownloads.refetch();

    if (refreshedDownloads.isError) {
      return null;
    }

    const refreshedFile = refreshedDownloads.data?.find(item => item.id === file.id);

    if (!refreshedFile) {
      return null;
    }

    const refreshedIsValid = await validateDownloadUrl(refreshedFile.url_download);

    if (!refreshedIsValid) {
      return null;
    }

    return refreshedFile.url_download;
  };

  const downloadFile = useMutation({
    mutationFn: async (file: DownloadFile) => {
      const url = await getDownloadUrl(file);

      if (!url) {
        toast.error('Link de download inválido');
        return;
      }

      try {
        const response = await fetch(url);

        if (!response.ok) {
          toast.error('Não foi possível baixar o arquivo');
          return;
        }

        const blob = await response.blob();
        await saveBlob(blob, buildFileName(file));
      } catch {
        toast.error('Não foi possível baixar o arquivo');
      }
    }
  });

  return {
    downloadFile: (file: DownloadFile) => downloadFile.mutateAsync(file),
    isDownloading: listDownloads.isFetching,
    downloadingUrl: downloadFile.isPending ? downloadFile.variables?.url_download : undefined,
    downloads: listDownloads.data
  };
};
